import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, CircleNotch, MagnifyingGlass, Check, SignOut } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { UserAvatar } from "./UserAvatar";
import { useAuth } from "../context/AuthContext";
import { authApi, uploadPhoto, formatApiError, listPlants } from "../lib/api";

const FIELD = "rounded-xl h-11 bg-muted/40 border-0";
const LABEL = "text-[11px] uppercase tracking-wider text-muted-foreground";
const BTN = "h-10 px-5 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-1.5 active:scale-[0.97] transition-[transform,opacity] disabled:opacity-60";
const NOTIFY = [["task_assigned", "Task assignments", "When someone assigns you a task"], ["mention", "@mentions", "When someone mentions you in a note"], ["cases", "Acute case updates", "Changes on cases you're part of"]];
const VIEWS = [["all", "All"], ["favorites", "Favorites"], ["critical", "Acute"], ["active", "Active"]];
const THEMES = [["system", "System"], ["light", "Light"], ["dark", "Dark"]];

function Seg({ options, value, onChange, testid }) {
  return (
    <div className="mt-2 inline-flex flex-wrap p-1 rounded-full bg-muted/70" data-testid={testid}>
      {options.map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`h-8 px-4 rounded-full text-xs transition-colors ${value === k ? "bg-background shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
      ))}
    </div>
  );
}

function FocusPicker({ focus, setFocus }) {
  const [plants, setPlants] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { listPlants().then((ps) => setPlants(ps.filter((p) => p.status !== "deceased"))).catch(() => {}); }, []);
  const locs = [...new Set(plants.map((p) => p.location).filter(Boolean))].sort();
  const toggle = (k, v) => setFocus((f) => ({ ...f, [k]: (f[k] || []).includes(v) ? f[k].filter((x) => x !== v) : [...(f[k] || []), v] }));
  const shown = plants.filter((p) => !q || `${p.nickname} ${p.plant_code}`.toLowerCase().includes(q.toLowerCase())).slice(0, 60);
  const count = (focus.plant_ids || []).length + (focus.locations || []).length;
  return (
    <div className="space-y-5">
      <p className="text-xs text-muted-foreground leading-relaxed">Pick the plants or spots you look after. Your To-do care reminders and acute case alerts will focus on them. Leave empty to see everything.</p>
      {locs.length > 0 && (
        <div>
          <p className={LABEL}>Locations</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {locs.map((l) => { const on = (focus.locations || []).includes(l); return (
              <button key={l} type="button" onClick={() => toggle("locations", l)} aria-pressed={on} data-testid={`focus-loc-${l}`}
                className={`h-8 px-3 rounded-full text-xs font-medium transition-colors ${on ? "bg-foreground text-background" : "bg-muted/70 hover:bg-muted"}`}>{l}</button>
            ); })}
          </div>
        </div>
      )}
      <div>
        <p className={LABEL}>Plants</p>
        <div className="mt-2 relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search plants" className={`${FIELD} pl-9 h-10`} data-testid="focus-search" />
        </div>
        <ul className="mt-2 max-h-56 overflow-y-auto rounded-2xl bg-muted/30 divide-y divide-border/50">
          {shown.map((p) => { const on = (focus.plant_ids || []).includes(p.id); return (
            <li key={p.id}>
              <button type="button" onClick={() => toggle("plant_ids", p.id)} data-testid={`focus-plant-${p.id}`} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50">
                <span className={`h-5 w-5 rounded-md flex items-center justify-center shrink-0 ${on ? "bg-primary text-primary-foreground" : "ring-1 ring-inset ring-border"}`}>{on && <Check weight="bold" className="h-3 w-3" />}</span>
                <span className="min-w-0 flex-1 text-sm truncate">{p.nickname}</span>
                <span className="text-[11px] text-muted-foreground truncate max-w-[40%]">{p.location || p.plant_code}</span>
              </button>
            </li>
          ); })}
        </ul>
      </div>
      <p className="text-xs font-medium" data-testid="focus-count">{count ? `Focusing on ${count} selection${count > 1 ? "s" : ""}` : "Seeing everything"}{count > 0 && <button type="button" onClick={() => setFocus({})} className="ml-3 text-primary hover:underline">Clear</button>}</p>
    </div>
  );
}

function Security({ onDone }) {
  const { logout } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState("");
  const change = async () => {
    if (pw.length < 8) return toast.error("Use at least 8 characters");
    setBusy("pw");
    try { await authApi.changePassword(cur, pw); toast.success("Password changed. Other devices were signed out."); setCur(""); setPw(""); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const everywhere = async () => {
    setBusy("all");
    try { await authApi.logoutAll(); await logout(); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-6">
      <div>
        <p className={LABEL}>Change password</p>
        <div className="mt-2 space-y-2">
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" autoComplete="current-password" className={FIELD} data-testid="security-current" />
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (8+ characters)" autoComplete="new-password" className={FIELD} data-testid="security-new" />
        </div>
        <button type="button" onClick={change} disabled={!cur || !pw || !!busy} className={`${BTN} mt-3 bg-foreground text-background`} data-testid="security-change-btn">{busy === "pw" && <CircleNotch className="h-4 w-4 animate-spin" />}Update password</button>
      </div>
      <div className="rounded-2xl bg-muted/40 p-4">
        <p className="text-sm font-semibold">Sign out of all devices</p>
        <p className="mt-0.5 text-xs text-muted-foreground">Ends every session, including this one. Use it if a phone was lost or shared.</p>
        <button type="button" onClick={everywhere} disabled={!!busy} className={`${BTN} mt-3 text-[#FF3B30] bg-[#FF3B30]/[0.08] hover:bg-[#FF3B30]/[0.14]`} data-testid="security-logout-all"><SignOut className="h-4 w-4" />Sign out everywhere</button>
      </div>
    </div>
  );
}

export default function ProfileDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [prefs, setPrefs] = useState({});
  const [focus, setFocus] = useState({});
  const [tab, setTab] = useState("profile");
  const [busy, setBusy] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (open && user) {
      setName(user.name || ""); setPhone(user.phone || ""); setAvatar(user.avatar_path || null);
      setPrefs(user.prefs || {}); setFocus(user.focus || {}); setTab("profile");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("upload");
    try { setAvatar((await uploadPhoto(file)).path); } catch { toast.error("Upload failed. Use a JPEG, PNG or WEBP image."); } finally { setBusy(""); }
  };
  const notify = prefs.notify || {};
  const setNotify = (k, v) => setPrefs((p) => ({ ...p, notify: { ...(p.notify || {}), [k]: v } }));

  const save = async () => {
    setBusy("save");
    try {
      setUser(await authApi.updateMe({ name: name.trim(), avatar_path: avatar, phone: phone.trim() || null, prefs, focus }));
      toast.success("Settings saved");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  if (!user) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-dialog" className="max-w-lg w-[calc(100vw-1.5rem)] max-h-[92dvh] overflow-y-auto rounded-3xl border-0 p-5 sm:p-6">
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl font-semibold tracking-tight">Your settings</DialogTitle>
          <DialogDescription>{user.email} · {user.role === "admin" ? "Administrator" : "Caregiver"}</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full grid grid-cols-4 rounded-full p-1 bg-muted h-auto">
            {[["profile", "Profile"], ["prefs", "Alerts"], ["focus", "My plants"], ["security", "Security"]].map(([k, l]) => (
              <TabsTrigger key={k} value={k} data-testid={`profile-tab-${k}`} className="rounded-full text-xs sm:text-sm py-1.5">{l}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="profile" className="mt-5 space-y-5">
            <div className="flex items-center gap-5">
              <div className="relative">
                <UserAvatar user={{ ...user, name: name || user.name, avatar_path: avatar }} className="h-20 w-20 text-xl" testid="profile-avatar-preview" />
                {busy === "upload" && <span className="absolute inset-0 rounded-full bg-background/70 flex items-center justify-center"><CircleNotch className="h-5 w-5 animate-spin" /></span>}
              </div>
              <div className="space-y-2">
                <button type="button" data-testid="profile-upload-avatar-btn" className="h-9 px-4 rounded-full bg-muted/70 hover:bg-muted text-sm font-medium inline-flex items-center gap-2" onClick={() => ref.current?.click()} disabled={!!busy}>
                  <Camera className="h-4 w-4" /> {avatar ? "Change photo" : "Upload photo"}
                </button>
                {avatar && <button data-testid="profile-remove-avatar-btn" className="block text-xs text-muted-foreground hover:text-foreground" onClick={() => setAvatar(null)}>Remove photo</button>}
              </div>
              <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="profile-avatar-input" />
            </div>
            <div><p className={LABEL}>Display name</p><Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className={`${FIELD} mt-2`} /></div>
            <div><p className={LABEL}>Phone</p><Input data-testid="profile-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value.slice(0, 30))} placeholder="Optional, visible to admins" className={`${FIELD} mt-2`} /></div>
          </TabsContent>
          <TabsContent value="prefs" className="mt-5 space-y-6">
            <div>
              <p className={LABEL}>Notifications</p>
              <ul className="mt-2 divide-y divide-border/60">
                {NOTIFY.map(([k, l, hint]) => (
                  <li key={k} className="py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium">{l}</p><p className="text-xs text-muted-foreground">{hint}</p></div>
                    <Switch checked={notify[k] !== false} onCheckedChange={(v) => setNotify(k, v)} data-testid={`pref-notify-${k}`} />
                  </li>
                ))}
              </ul>
            </div>
            <div><p className={LABEL}>Homepage opens on</p><Seg options={VIEWS} value={prefs.home_view || "all"} onChange={(v) => setPrefs((p) => ({ ...p, home_view: v }))} testid="pref-home-view" /></div>
            <div><p className={LABEL}>Appearance</p><Seg options={THEMES} value={prefs.theme || "system"} onChange={(v) => setPrefs((p) => ({ ...p, theme: v }))} testid="pref-theme" /></div>
          </TabsContent>
          <TabsContent value="focus" className="mt-5"><FocusPicker focus={focus} setFocus={setFocus} /></TabsContent>
          <TabsContent value="security" className="mt-5"><Security onDone={() => onOpenChange(false)} /></TabsContent>
        </Tabs>
        {tab !== "security" && (
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="h-10 px-4 rounded-full text-sm bg-muted hover:bg-muted/70" onClick={() => onOpenChange(false)} data-testid="profile-cancel-btn">Cancel</button>
            <button type="button" className={`${BTN} bg-foreground text-background`} onClick={save} disabled={!!busy || !name.trim()} data-testid="profile-save-btn">
              {busy === "save" && <CircleNotch className="h-4 w-4 animate-spin" />} Save
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
